import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const BrandValues = () => {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('opacity-100', 'translate-y-0');
              entry.target.classList.remove('opacity-0', 'translate-y-10');
            }, i * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const metodoSteps = [
    {
      num: '01',
      title: 'Diagnóstico sin filtros',
      desc: 'Analizamos tu empresa desde dentro: qué está funcionando, qué está fallando y dónde está el dinero que estás dejando sobre la mesa. Sin teoría, con datos reales.'
    },
    {
      num: '02',
      title: 'Estrategia a medida, no plantillas',
      desc: 'Diseñamos un plan específico para TU empresa, TU sector y TUS objetivos. No existe una solución estándar porque ninguna empresa es estándar.'
    },
    {
      num: '03',
      title: 'Implementación con velocidad de startup',
      desc: 'Ejecutamos rápido. En semanas, no meses. Priorizamos las acciones que generan impacto inmediato mientras construimos las soluciones a largo plazo.'
    },
    {
      num: '04',
      title: 'Medición obsesiva de resultados',
      desc: 'Dashboards en tiempo real, reporting mensual y reuniones de estrategia. Siempre sabes exactamente qué está pasando y hacia dónde vamos.'
    },
    {
      num: '05',
      title: 'Optimización continua sin parar',
      desc: 'El mercado cambia. Nosotros también. Iteramos, mejoramos y escalamos lo que funciona con la misma urgencia del primer día.'
    }
  ];

  return (
    <>
      {/* MÉTODO SECTION */}
      <section id="metodo" className="section-padding bg-[#0A0A0A] relative">
        <div className="container-custom">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-16 lg:gap-20 items-start">
            {/* Left - Sticky */}
            <div ref={addRef} className="opacity-0 translate-y-10 transition-all duration-700 ease-out lg:sticky lg:top-32">
              {/* Label */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-[1px] bg-[#D4AF37]"></div>
                <span className="font-outfit text-[0.72rem] uppercase tracking-[0.25em] text-[#D4AF37] font-medium">
                  Nuestro método
                </span>
              </div>

              <h2 className="heading-1 mb-4">
                <span className="sr-only">Consultoría Tecnológica y Transformación Digital en Huelva y Jaén - </span>
                Así es cómo convertimos tu empresa en una máquina de resultados
              </h2>

              <p className="text-sm leading-relaxed text-[#777] mt-4">
                Un proceso claro, sin sorpresas, diseñado para generar impacto real desde el primer mes.
              </p>

              <button
                onClick={() => scrollToSection('#contacto')}
                className="inline-flex items-center gap-2.5 bg-[#D4AF37] text-[#080808] font-outfit font-bold text-sm uppercase tracking-wider px-9 py-4 transition-all duration-300 hover:bg-[#E8C97A] hover:translate-y-[-2px] mt-9"
                style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)' }}
              >
                Empezar ahora
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right - Steps */}
            <div className="flex flex-col">
              {metodoSteps.map((step, index) => (
                <div
                  key={index}
                  ref={addRef}
                  className="opacity-0 translate-y-10 transition-all duration-700 ease-out py-9 border-b border-white/[0.06] grid grid-cols-[80px_1fr] gap-7 items-start group"
                >
                  <div className="font-outfit font-black text-5xl leading-none text-white/[0.07] group-hover:text-[#D4AF37] transition-colors duration-300">
                    {step.num}
                  </div>
                  <div>
                    <strong className="block font-outfit font-bold text-lg mb-2.5 text-white">
                      {step.title}
                    </strong>
                    <p className="text-sm leading-relaxed text-[#777]">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GARANTÍA SECTION */}
      <section className="py-20 px-6 md:px-16 bg-[#D4AF37] text-center">
        <h2
          ref={addRef}
          className="opacity-0 translate-y-10 transition-all duration-700 ease-out font-outfit font-black text-[#080808] leading-tight max-w-[780px] mx-auto mb-5"
          style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)' }}
        >
          Si no hay resultados, no hay sentido en continuar juntos.
        </h2>
        <p
          ref={addRef}
          className="opacity-0 translate-y-10 transition-all duration-700 ease-out delay-100 text-base text-[#080808]/70 max-w-[580px] mx-auto mb-9 leading-relaxed"
        >
          Trabajamos con la mentalidad de socios, no de proveedores. Eso significa que asumimos la responsabilidad de los resultados contigo. Sin excusas, sin escudarnos en circunstancias externas.
        </p>
        <button
          ref={addRef}
          onClick={() => scrollToSection('#contacto')}
          className="opacity-0 translate-y-10 transition-all duration-700 ease-out delay-200 inline-flex items-center gap-2.5 bg-[#080808] text-[#D4AF37] font-outfit font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-[#111] hover:translate-y-[-2px] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] rounded-sm"
        >
          Quiero un equipo que se comprometa de verdad
          <ArrowRight className="w-4 h-4" />
        </button>
      </section>
    </>
  );
};

export default BrandValues;
